import { AnimatePresence, motion } from "framer-motion";
import { AddOrEditCompany } from "../../../queries/models/form-company.model";
import * as Yup from "yup";
import { phoneRegExp } from "../../../utils/phoneRegExp";
import { Form, Formik } from "formik";
import FormikInputText from "../formik/FormikInputText";
import { useAddCompanyMutation } from "../../../queries/users.query";
import { useCallback, useState } from "react";
import { useToast } from "../toast-context-provider";
import { CreateCompanyRequest } from "../../../queries/models/create-company-request.model";
import { btnVariants, btnStyles } from "../FramerMotion/button-states";
import { RegisterFormProps } from "./RegisterForm";
import FormikInputPassword from "../formik/formikInputPassword";
import { CompanyRegistered } from "./CompanyRegistered";
import ReCAPTCHA from "react-google-recaptcha";

export function RegisterMobileForm({
  isVisible,
  setIsVisible,
  companyRegistered,
  setCompanyRegistered,
}: RegisterFormProps) {
  const addCompanyMutation = useAddCompanyMutation();
  const [verificationToken, setVerificationToken] = useState<string>();
  const toast = useToast();

  const initialValues: AddOrEditCompany = {
    phone: "",
    email: "",
    password: "",
    passwordRepeat: "",

    companyName: "",
  };

  const validationSchema = Yup.object({
    phone: Yup.string().matches(phoneRegExp, "Phone number format invalid"),
    email: Yup.string().required().email("Email format invalid"),
    password: Yup.string()
      .required("Required")
      .min(8, "Must be 8 characters or more"),
    passwordRepeat: Yup.string()
      .required("Required")
      .oneOf([Yup.ref("password")], "Passwords must match"),
    companyName: Yup.string()
      .min(3, "Must be 3 characters or more")
      .required("Required"),
  });

  const saveNewCompany = useCallback(
    (data: AddOrEditCompany) => {
      const mutateOptions = {
        onSuccess: async () => {
          toast.current?.show({
            severity: "success",
            detail: "Save success",
          });
          setCompanyRegistered(true);
        },
        onError: async (error: any) => {
          toast.current?.show({
            severity: "error",
            detail: error?.data,
          });
        },
      };

      const request: CreateCompanyRequest = {
        phone: data.phone,
        email: data.email,
        password: data.password,

        companyName: data.companyName,
        recaptchaToken: verificationToken ?? "",
      };

      return addCompanyMutation.mutateAsync(request, mutateOptions);
    },
    [addCompanyMutation, setCompanyRegistered, toast, verificationToken]
  );

  const handleCaptchaChange = useCallback((response: any) => {
    setVerificationToken(response);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && !companyRegistered && (
        <motion.div
          initial={{ opacity: "0%" }}
          animate={{ opacity: "100%" }}
          exit={{ opacity: "0%" }}
          transition={{ ease: "linear", duration: 0.4 }}
          className="w-full flex h-full"
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={saveNewCompany}
            enableReinitialize
            validateOnChange
            validateOnMount
          >
            {(formik) => (
              <Form
                autoComplete="off"
                className="grid p-4 h-max m-0"
                style={{ color: "white" }}
              >
                <div
                  className="col-12"
                  style={{ height: "96px" }}
                >
                  <FormikInputText
                    label="Company name"
                    name="companyName"
                    validationSchema={validationSchema}
                    autoComplete="off"
                    withIcon
                    disabled={!isVisible}
                  />
                </div>
                <div
                  className="col-6"
                  style={{ height: "96px" }}
                >
                  <FormikInputText
                    label="Email"
                    name="email"
                    validationSchema={validationSchema}
                    autoComplete="off"
                    withIcon
                    disabled={!isVisible}
                  />
                </div>
                <div
                  className="col-6"
                  style={{ height: "96px" }}
                >
                  <FormikInputText
                    label="Phone"
                    name="phone"
                    validationSchema={validationSchema}
                    autoComplete="off"
                    withIcon
                    disabled={!isVisible}
                  />
                </div>
                <div
                  className="col-6"
                  style={{ height: "96px" }}
                >
                  <FormikInputPassword
                    label="Password"
                    name="password"
                    validationSchema={validationSchema}
                    autoComplete="off"
                    withIcon
                    disabled={!isVisible}
                    feedback={false}
                    toggleMask
                  />
                </div>
                <div
                  className="col-6"
                  style={{ height: "96px" }}
                >
                  <FormikInputPassword
                    label="Repeat password"
                    name="passwordRepeat"
                    validationSchema={validationSchema}
                    autoComplete="off"
                    withIcon
                    disabled={!isVisible}
                    feedback={false}
                    toggleMask
                  />
                </div>
                <div
                  className="col-12 flex p-0 mt-3 justify-content-center"
                  style={{ height: "96px" }}
                >
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                    size="normal"
                    onChange={handleCaptchaChange}
                  />
                </div>
                <div
                  className="col-12 flex p-0 mt-6"
                  style={{ height: "96px" }}
                >
                  <div className="col-6">
                    <AnimatePresence>
                      <motion.button
                        style={btnStyles.paOutlinedButton}
                        initial="iniOutlinedButton"
                        whileHover="hovOutlinedButton"
                        variants={btnVariants}
                        onClick={() => {
                          setIsVisible(isVisible);
                        }}
                        type="button"
                      >
                        Back
                      </motion.button>
                    </AnimatePresence>
                  </div>
                  <div className="col-6">
                    <AnimatePresence>
                      <motion.button
                        style={btnStyles.paButton}
                        initial="iniButton"
                        whileHover="hovButton"
                        variants={btnVariants}
                        disabled={!formik.isValid || !verificationToken}
                        type="submit"
                      >
                        Sign up
                      </motion.button>
                    </AnimatePresence>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </motion.div>
      )}
      {isVisible && companyRegistered && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: "0%" }}
          exit={{ x: "100%" }}
          transition={{ ease: "linear", duration: 0.6 }}
          className="w-full flex h-full"
          style={{ borderRadius: "0px 20px 20px 0px" }}
        >
          <CompanyRegistered
            onShowLogin={() => {
              setIsVisible(isVisible);
              setCompanyRegistered(!companyRegistered);
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
